import { useEvent } from "../context/EventProvider";
import { useMode } from "../panel/mode";
import { readAnswers, type Answers } from "./answers";
import { locked, type Article, type Selection } from "./articles";
import type { Me, Recorded } from "./participant";

/**
 * ═══ LE BROUILLON D'UN PREMIER PARCOURS : PROFIL, JOURS, BILLETS (tâche #209) ═══
 *
 * Quelqu'un qui quittait l'inscription en route retrouvait au retour ses informations,
 * sa société et ses conférences (ces étapes écrivent tout de suite), mais devait
 * rechoisir son profil, ses jours et ses billets : ces trois réponses ne vivaient que
 * dans `history.state`, qu'une nouvelle visite ne porte pas.
 *
 * ⚠️ UN BROUILLON, ET NON UNE ÉCRITURE AU SERVEUR. Ne les écrire qu'à la confirmation est
 * voulu (voir `stepSave.tsx` et `routes/Dates.tsx`) : écrire les jours inscrit pour de
 * bon, et la route des billets DÉTRUIT la commande avant de la recréer. Le brouillon
 * reste donc dans CE navigateur (`localStorage`), et personne n'est inscrit avant d'avoir
 * confirmé. Le revers, assumé : il ne suit pas la personne sur un autre appareil.
 *
 * ⚠️ IL NE DÉCIDE DE RIEN, IL PRÉ-COCHE. L'étape le montre quand ce parcours n'a encore
 * rien répondu ; il ne devient une réponse que lorsque son « Continuer » l'emporte, comme
 * tout ce qu'on coche. Rien en aval (confirmation, fil d'Ariane, garde de sortie) ne le
 * lit : ce qui écrit reste l'état de navigation.
 *
 * ⚠️ PREMIER PARCOURS SEULEMENT (`responded` faux). Une inscription enregistrée est la
 * vérité ; une correction abandonnée a son propre garde (`leaveGuard.ts`), et la montrer
 * au retour par-dessus l'enregistré ferait croire qu'elle l'est.
 *
 * ⚠️ JAMAIS EN MODE ORGANISATEUR, ni lu ni écrit : ses clics règlent le parcours, ce ne
 * sont pas des réponses (la leçon de la tâche #195, `routes/Confirmation.tsx`).
 *
 * ⚠️ EFFACÉ DÈS QUE L'INSCRIPTION EST ÉCRITE (`useParticipation`) : passé ce point, il
 * contredirait ce que le serveur détient.
 *
 * ⚠️ ET LU COMME `history.state`, SANS CONFIANCE : n'importe quel script de la page peut
 * l'avoir écrit. Il passe par `readAnswers`, puis chaque étape le confronte à ce qu'elle
 * propose aujourd'hui (un profil retiré, un jour supprimé, un billet plus en vente).
 */
export type Draft = Pick<Answers, "typoId" | "days" | "articles">;

const draftKey = (domainId: number, eventId: number, userId: number) =>
  `gk-funnel-draft:${domainId}:${eventId}:${userId}`;

/** Le brouillon rangé, ramené aux trois réponses qu'il peut porter. */
export function readDraft(raw: string | null): Draft {
  if (!raw) return {};
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return {};
  }
  const { typoId, days, articles } = readAnswers(parsed);
  return {
    ...(typoId !== undefined ? { typoId } : {}),
    ...(days !== undefined ? { days } : {}),
    ...(articles !== undefined ? { articles } : {}),
  };
}

/* Stockage indisponible (navigation privée, quota) : pas de brouillon, rien ne casse. */
function load(key: string): string | null {
  try {
    return globalThis.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function store(key: string, patch: Draft): void {
  try {
    globalThis.localStorage.setItem(
      key,
      JSON.stringify({ ...readDraft(load(key)), ...patch }),
    );
  } catch {
    /* le choix ne survivra pas à la visite, comme avant */
  }
}

/** L'inscription vient d'être écrite : le brouillon n'a plus d'objet. */
export function forgetDraft(domainId: number, eventId: number, userId: number): void {
  try {
    globalThis.localStorage.removeItem(draftKey(domainId, eventId, userId));
  } catch {
    /* rien à effacer */
  }
}

/**
 * Le brouillon de cette personne pour cet événement, et de quoi y ranger un choix. Vide,
 * et `keep` sans effet, hors d'un premier parcours en visiteur — ou tant que
 * l'inscription n'est pas lue, puisqu'on ne sait pas encore si c'en est un.
 */
export function useDraft(
  me: Me | undefined,
  recorded: Recorded | undefined,
): { draft: Draft; keep: (patch: Draft) => void } {
  const event = useEvent();
  const mode = useMode();
  const key =
    mode === "visitor" &&
    event &&
    typeof me?.id === "number" &&
    recorded &&
    recorded.responded !== true
      ? draftKey(event.domainId, event.eventId, me.id)
      : null;
  return {
    draft: key ? readDraft(load(key)) : {},
    keep: (patch) => {
      if (key) store(key, patch);
    },
  };
}

/** Le profil du brouillon, s'il est encore proposé. */
export function draftTypo(
  draft: Draft,
  offered: ReadonlyArray<number>,
): number | undefined {
  return draft.typoId !== undefined && offered.includes(draft.typoId)
    ? draft.typoId
    : undefined;
}

/**
 * Les jours du brouillon qui existent encore. ⚠️ RIEN QUAND IL N'EN RESTE AUCUN : une
 * liste vide cocherait zéro jour par-dessus ceux que le serveur retient peut-être déjà.
 */
export function draftDays(
  draft: Draft,
  all: ReadonlyArray<number>,
): number[] | undefined {
  const kept = (draft.days ?? []).filter((id) => all.includes(id));
  return kept.length > 0 ? kept : undefined;
}

/**
 * ═══ LA COMMANDE DU BROUILLON, TELLE QU'ON PEUT LA RENDRE AUJOURD'HUI ═══
 *
 * ⚠️ LE BROUILLON NE REND QUE CE QUE LE PARTICIPANT POUVAIT CHOISIR. Ce que le back
 * office impose (pré-sélectionné non modifiable) vient du serveur, tel qu'il est
 * maintenant : posé depuis l'abandon, le brouillon l'aurait fait disparaître, et sa case
 * verrouillée ne se recoche pas.
 *
 * ⚠️ ET UN BILLET QUI N'EST PLUS EN VENTE TOMBE, comme un article inconnu du catalogue.
 * Gardé, l'étalage le montrerait (« ce qu'on a déjà ») verrouillé, donc impossible à
 * retirer : on vendrait ce que l'organisateur a retiré de la vente. Voir `locked`.
 */
export function draftSelection(
  draft: Draft,
  saved: Selection | undefined,
  articles: ReadonlyArray<Article>,
): Selection | undefined {
  if (!draft.articles) return undefined;
  const byId = new Map(articles.map((a) => [a.id, a]));
  const merged: Selection = {};
  const add = (day: string, id: number) => {
    const ids = (merged[day] ??= []);
    if (!ids.includes(id)) ids.push(id);
  };

  for (const [day, ids] of Object.entries(saved ?? {})) {
    for (const id of ids) {
      const article = byId.get(id);
      if (!article || locked(article, Number(day))) add(day, id);
    }
  }
  for (const [day, ids] of Object.entries(draft.articles)) {
    for (const id of ids) {
      const article = byId.get(id);
      if (article && !locked(article, Number(day))) add(day, id);
    }
  }
  return merged;
}
